<template>
    <div class="mx-auto max-w-screen-2xl">
        <div>
            <ul role="list" class="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <StripeSubscriptionBlock
                    v-if="transactions?.subscription && transactions?.subscription.customer"
                    :subscription="transactions?.subscription"
                    :user="props.user"
                    @viewEnablements="showEnablements"
                    @reload="loadTransactions"
                ></StripeSubscriptionBlock>

                <InvoiceSubscriptionBlock
                    v-else-if="transactions?.subscription && !transactions?.subscription.payment_ref"
                    :subscription="transactions?.subscription"
                    @viewEnablements="showEnablements"
                ></InvoiceSubscriptionBlock>

                <EmptySubscriptionBlock
                    v-else
                    :user="props.user"
                ></EmptySubscriptionBlock>

                <PurchaseInfoBlock
                    :user="props.user"
                    @viewLicences="showLicences"
                    @viewCredits="showCredits"
                ></PurchaseInfoBlock>
            </ul>

            <div class="flex flex-col mt-12" v-if="transactions?.invoices && transactions?.invoices.data.length > 0">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-100">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Product
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cost
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Edit</span>
                                    </th>
                                </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="invoice in transactions.invoices.data" :key="invoice.id">
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                            <div class="flex items-center">
                                                <div class="text-sm font-medium text-gray-900">
                                                    <span>{{ invoice.customer_email }}</span>
                                                </div>
                                            </div>
                                        </router-link>
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                            <div class="text-sm font-medium text-gray-900">
                                                <span class="block" v-for="item in invoice.lines.data" :key="item.id">
                                                    <ArrowPathIcon class="h-4 w-4 mr-2 text-gray-400 inline"
                                                                 v-if="item.subscription"/> {{ item.description }}
                                                </span>
                                            </div>
                                        </router-link>
                                    </td>

                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                            <div class="flex items-center">
                                                <div class="text-sm text-gray-900">
                                                    {{ currencyFormat(invoice.currency, invoice.total) }}
                                                </div>
                                                <span v-if="invoice.payment_intent?.charges?.data[0]?.amount_refunded > 0"
                                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-4">Refunded</span>
                                                <span v-else-if="['uncollectible'].includes(invoice.status)"
                                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 ml-4">Lapsed</span>
                                                <span v-else-if="['void'].includes(invoice.status)"
                                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-4">Void</span>
                                                <span v-else-if="['open', 'draft'].includes(invoice.status)"
                                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 ml-4">Open</span>
                                                <span v-else-if="['paid'].includes(invoice.status)"
                                                      class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-4">Paid</span>
                                            </div>
                                        </router-link>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <router-link :to="{ name: 'sales.view', params: { invoice: invoice.id }}">
                                            <EyeIcon class="h-5 w-5 text-gray-400 hover:text-gray-600"/>
                                        </router-link>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <Enablements
                v-if="transactions?.enablements"
                :enablements="transactions?.enablements"
                :open="showingEnablements"
                @close="showingEnablements = false"
            ></Enablements>

            <Licences
                :open="showingLicences"
                @close="showingLicences = false"
            ></Licences>

            <Credits
                :open="showingCredits"
                @close="showingCredits = false"
            >
            </Credits>
        </div>

        <div v-if="!loadingTransactions && !transactions" class="mt-8">
            <p>This user has yet to make any transactions within JournoLink</p>
        </div>
    </div>
</template>

<script>
import {ref, onMounted} from "vue"
import {CurrencyPoundIcon, EyeIcon, EnvelopeIcon, PhoneIcon, ArrowPathIcon, MagnifyingGlassCircleIcon} from "@heroicons/vue/24/outline"
import Enablements from "./Enablements"
import StripeSubscriptionBlock from "./StripeSubscriptionBlock"
import InvoiceSubscriptionBlock from "./InvoiceSubscriptionBlock"
import EmptySubscriptionBlock from "./EmptySubscriptionBlock"
import PurchaseInfoBlock from "./PurchaseInfoBlock";
import Licences from "./Licences";
import Credits from "./Credits";
import {useRoute} from "vue-router";
import {useStore} from "vuex";

export default {
    name: "Transactions",
    components: {
        Licences,
        Credits,
        EmptySubscriptionBlock,
        InvoiceSubscriptionBlock,
        StripeSubscriptionBlock,
        PurchaseInfoBlock,
        Enablements,
        PhoneIcon,
        EnvelopeIcon,
        EyeIcon,
        ArrowPathIcon,
        MagnifyingGlassCircleIcon,
        CurrencyPoundIcon,
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const store = useStore()
        const transactions = ref(null)
        const showingEnablements = ref(false)
        const showingLicences = ref(false)
        const showingCredits = ref(false)
        const loadingTransactions = ref(true)

        const loadTransactions = async () => {
            await axios.get(route('transactions.customer', { user: props.user.id }))
                .then((response) => {
                    if (Object.keys(response.data).length > 0) {
                        transactions.value = response.data
                    }
                    loadingTransactions.value = false
                }).catch(error => {
                    loadingTransactions.value = false
                });
        }

        const showEnablements = () => {
            showingEnablements.value = true
        }

        const showLicences = () => {
            showingLicences.value = true
        }

        const showCredits = () => {
            showingCredits.value = true
        }

        onMounted(() => {
            window.location.hash = 'purchases'
            loadTransactions()
            store.dispatch('populateUserAdditionals')
        })

        return {
            props,
            transactions,
            showEnablements,
            showingEnablements,
            showLicences,
            showingLicences,
            showCredits,
            showingCredits,
            loadTransactions,
            loadingTransactions,
        }
    }
}
</script>

<style scoped>

</style>
